<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-1 gap-2">
      <div
        v-for="method in storedPaymentMethods"
        :key="method.id"
        class="rounded-md bg-obsidian-50 px-4 py-3 text-sm"
      >
        <div class="flex flex-wrap items-start justify-between gap-2">
          <p>{{ formatStoredPaymentMethodLabel(method) }}</p>
          <CVButton
            v-if="booking.hasPeriodicInvoicing && !method.purposes.includes(CreditCard.PERIODIC)"
            variant="secondary"
            :is-loading="promotingMethodId === method.id"
            :disabled="isBusy || !!promotingMethodId"
            @click.prevent="promoteToPeriodic(method)"
          >
            {{ t('promoteToPeriodic') }}
          </CVButton>
        </div>
        <Collapsible
          v-if="method.ccDetails"
          v-model="expandedCreditCardIds[method.id]"
          :title="t('creditCardDetails')"
          variant="compact"
          class="mt-2"
        >
          <CreditCardDetails :credit-card-details="method.ccDetails" />
        </Collapsible>
      </div>
    </div>
    <EntityListExceptions
      :status="status"
      :error="error"
      :items-count="storedPaymentMethods.length"
      :no-items-text="t('noStoredPaymentMethods')"
      small
    />
    <div v-if="addMode === 'none'" class="flex flex-wrap justify-end gap-2">
      <CVButton
        v-if="isLongTerm"
        variant="secondary"
        :disabled="!canAddIban"
        @click.prevent="openIbanForm"
      >
        {{ t('addIban') }}
      </CVButton>
      <CVButton variant="default" @click.prevent="openLinkForm">
        {{ t('addNew') }}
      </CVButton>
    </div>
    <div v-else-if="addMode === 'link'" class="flex flex-col gap-4">
      <Select
        v-model="purpose"
        class="min-w-48"
        :label="t('purpose')"
        :options="purposeOptions"
        :disabled="isBusy"
      />
      <div class="flex flex-wrap justify-end gap-4">
        <CVButton
          variant="default"
          :is-loading="isSending"
          :disabled="!purpose || isBusy"
          @click.prevent="sendStorePaymentMethodLink"
        >
          {{ t('sendStorePaymentMethodPaymentLink') }}
        </CVButton>
        <CVButton variant="secondary" :disabled="isBusy" @click.prevent="hideAddForm">
          {{ t('cancel') }}
        </CVButton>
      </div>
    </div>
    <div v-else class="flex flex-col gap-4">
      <Select
        v-model="ibanOwner"
        class="min-w-48"
        :label="t('owner')"
        :options="ibanOwnerOptions"
        :disabled="isBusy"
      />
      <Input v-model="iban" :label="t('iban')" :disabled="isBusy" autocomplete="off" />
      <Input
        v-model="accountHolderName"
        :label="t('accountHolderName')"
        :disabled="isBusy"
        autocomplete="name"
      />
      <Checkbox v-model="mandateSigned" :disabled="isBusy" :label="t('mandateSigned')" />
      <p class="text-xs text-obsidian-500">{{ t('mandateConfirmationHint') }}</p>
      <div class="flex flex-wrap justify-end gap-4">
        <CVButton
          variant="default"
          :is-loading="isStoringIban"
          :disabled="!canSubmitIban || isBusy"
          @click.prevent="storeIban"
        >
          {{ t('storeIban') }}
        </CVButton>
        <CVButton variant="secondary" :disabled="isBusy" @click.prevent="hideAddForm">
          {{ t('cancel') }}
        </CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Booking } from '@/entities/bookings/booking.entity';
import {
  usePromoteStoredPaymentMethodToPeriodicCharge,
  useStoreIbanPaymentMethod,
  useStorePaymentMethod,
  useStoredPaymentMethods,
} from '@/queries/use-payment-api';
import { Alert } from '@/services/alert.service';
import { formatStoredPaymentMethodLabel } from '@/utils/format-stored-payment-method-label';
import { isLongTermPricing } from '@/utils/pricing.utils';
import {
  selectPaymentLinkReceiver,
  showPaymentLinkToCopy,
} from '@/utils/select-payment-link-receiver';
import {
  CounterpartyType,
  CreditCard,
  SendPaymentLinkResult,
  type BaseStoredPaymentMethod,
} from '@carvia/ros-client-types';
import { UserRole } from '@/entities/auth/user-role.enum.ts';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Collapsible from '../Collapsible.vue';
import EntityListExceptions from '../EntityListExceptions.vue';
import Input from '../Input.vue';
import Select from '../Select.vue';
import CVButton from '../buttons/CVButton.vue';
import Checkbox from '@/components/Checkbox.vue';
import CreditCardDetails from './CreditCardDetails.vue';

const props = defineProps<{
  booking: Booking;
}>();

const { t } = useI18n();
const { t: tGlobal } = useI18n({ useScope: 'global' });
const defaultPurpose = () =>
  props.booking.hasPeriodicInvoicing ? CreditCard.PERIODIC : CreditCard.ONLINE_PAYMENT;

const purpose = ref<CreditCard>(defaultPurpose());
const addMode = ref<'none' | 'link' | 'iban'>('none');
const ibanOwner = ref<CounterpartyType>(CounterpartyType.CUSTOMER);
const iban = ref('');
const mandateSigned = ref(false);
const accountHolderName = ref(
  [props.booking.customerData?.firstName, props.booking.customerData?.lastName]
    .filter(Boolean)
    .join(' '),
);
const expandedCreditCardIds = reactive<Record<string, boolean>>({});
const promotingMethodId = ref<string | null>(null);

const { data: storedPaymentMethodsData, status, error } = useStoredPaymentMethods(props.booking.id);
const storedPaymentMethods = computed(() => storedPaymentMethodsData.value ?? []);

const { mutateAsync: storePaymentMethod, isPending: isSending } = useStorePaymentMethod();
const { mutateAsync: storeIbanPaymentMethod, isPending: isStoringIban } =
  useStoreIbanPaymentMethod();
const { mutateAsync: promoteStoredPaymentMethod } = usePromoteStoredPaymentMethodToPeriodicCharge();

const isBusy = computed(() => isSending.value || isStoringIban.value);
const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));
const contactEmail = computed(
  () => props.booking.customerData?.contactEmail?.trim() || props.booking.customer?.email,
);
const canCreateCustomerFromData = computed(() => !props.booking.customer && !!contactEmail.value);
const canAddIban = computed(
  () => !!props.booking.customer || canCreateCustomerFromData.value || !!props.booking.agency,
);
const defaultIbanOwner = () =>
  props.booking.customer || canCreateCustomerFromData.value
    ? CounterpartyType.CUSTOMER
    : CounterpartyType.AGENCY;
const canSubmitIban = computed(
  () =>
    !!ibanOwner.value &&
    iban.value.replace(/\s+/g, '').length >= 15 &&
    accountHolderName.value.trim().length > 0 &&
    mandateSigned.value,
);

const purposeOptions = computed(() => {
  const options = [
    { label: tGlobal('creditCard.ONLINE_PAYMENT'), value: CreditCard.ONLINE_PAYMENT },
  ];
  if (props.booking.hasPeriodicInvoicing) {
    options.push({ label: tGlobal('creditCard.PERIODIC'), value: CreditCard.PERIODIC });
  }
  return options;
});

const ibanOwnerOptions = computed(() => {
  const options: { label: string; value: CounterpartyType }[] = [];
  if (props.booking.customer) {
    options.push({
      label: tGlobal('counterpartyType.customer'),
      value: CounterpartyType.CUSTOMER,
    });
  } else if (canCreateCustomerFromData.value) {
    options.push({
      label: t('createCustomerFromData'),
      value: CounterpartyType.CUSTOMER,
    });
  }
  if (props.booking.agency) {
    options.push({
      label:
        props.booking.agency.role === UserRole.COMPANY
          ? t('ownerCompany')
          : tGlobal('counterpartyType.agency'),
      value: CounterpartyType.AGENCY,
    });
  }
  return options;
});

const hideAddForm = () => {
  addMode.value = 'none';
  purpose.value = defaultPurpose();
  iban.value = '';
  mandateSigned.value = false;
  ibanOwner.value = defaultIbanOwner();
};

const openIbanForm = () => {
  ibanOwner.value = defaultIbanOwner();
  addMode.value = 'iban';
};

const openLinkForm = () => {
  purpose.value = defaultPurpose();
  addMode.value = 'link';
};

const sendStorePaymentMethodLink = async () => {
  const paymentLinkReceiver = await selectPaymentLinkReceiver(props.booking);
  if (!paymentLinkReceiver || !purpose.value) return;

  const { paymentLink } = await storePaymentMethod({
    bookingId: props.booking.id,
    purpose: purpose.value,
    paymentLinkReceiver,
  });
  if (paymentLink?.sendPaymentLinkResult === SendPaymentLinkResult.DISABLED && paymentLink.url) {
    await showPaymentLinkToCopy(paymentLink.url);
  }
  hideAddForm();
};

const storeIban = async () => {
  if (!canSubmitIban.value) return;
  await storeIbanPaymentMethod({
    bookingId: props.booking.id,
    iban: iban.value,
    accountHolderName: accountHolderName.value.trim(),
    mandateSigned: true,
    purpose: defaultPurpose(),
    counterpartyType: ibanOwner.value,
  });
  hideAddForm();
};

const promoteToPeriodic = async (method: BaseStoredPaymentMethod) => {
  const alertResult = await Alert.fire({
    titleText: t('promoteToPeriodicTitle'),
    text: t('promoteToPeriodicText'),
    icon: 'question',
    showCancelButton: true,
    cancelButtonText: t('cancel'),
    confirmButtonText: t('promoteToPeriodic'),
  });
  if (!alertResult.isConfirmed) return;

  promotingMethodId.value = method.id;
  try {
    await promoteStoredPaymentMethod({
      bookingId: props.booking.id,
      storedPaymentMethodId: method.id,
    });
  } finally {
    promotingMethodId.value = null;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "noStoredPaymentMethods": "This booking has no stored payment methods",
    "purpose": "Purpose",
    "addNew": "Request new payment method",
    "addIban": "Add IBAN",
    "owner": "Counterparty",
    "ownerCompany": "Company account",
    "createCustomerFromData": "Create customer from customer data",
    "iban": "IBAN",
    "accountHolderName": "Account holder",
    "mandateSigned": "Signed SEPA Direct Debit mandate is on file",
    "mandateConfirmationHint": "The account holder will receive a confirmation with the mandate reference.",
    "storeIban": "Store IBAN",
    "sendStorePaymentMethodPaymentLink": "Send store payment method link",
    "cancel": "Cancel",
    "creditCardDetails": "Credit Card Details",
    "promoteToPeriodic": "Use for recurring billing",
    "promoteToPeriodicTitle": "Set as recurring billing method?",
    "promoteToPeriodicText": "This payment method will be used for recurring billing. Any other method currently marked for recurring billing will lose that designation."
  },
  "de": {
    "noStoredPaymentMethods": "Diese Buchung hat keine hinterlegten Zahlungsmethoden",
    "purpose": "Verwendungszweck",
    "addNew": "Neue Zahlungsmethode anfordern",
    "addIban": "IBAN hinterlegen",
    "owner": "Gegenpartei",
    "ownerCompany": "Firmenkonto",
    "createCustomerFromData": "Kunde aus Kundendaten anlegen",
    "iban": "IBAN",
    "accountHolderName": "Kontoinhaber",
    "mandateSigned": "Unterschriebenes SEPA-Lastschriftmandat liegt vor",
    "mandateConfirmationHint": "Der Kontoinhaber erhält eine Bestätigung mit der Mandatsreferenz.",
    "storeIban": "IBAN speichern",
    "sendStorePaymentMethodPaymentLink": "Zahlungslink zum Hinterlegen senden",
    "cancel": "Abbrechen",
    "creditCardDetails": "Kreditkarten Details",
    "promoteToPeriodic": "Für regelmäßige Abrechnung verwenden",
    "promoteToPeriodicTitle": "Als regelmäßige Abrechnung festlegen?",
    "promoteToPeriodicText": "Diese Zahlungsmethode wird für regelmäßige Abrechnungen verwendet. Bei anderen Methoden wird diese Kennzeichnung entfernt."
  },
  "es": {
    "noStoredPaymentMethods": "Esta reserva no tiene métodos de pago guardados",
    "purpose": "Uso",
    "addNew": "Solicitar nuevo método de pago",
    "addIban": "Añadir IBAN",
    "owner": "Contraparte",
    "ownerCompany": "Cuenta de empresa",
    "createCustomerFromData": "Crear cliente a partir de los datos",
    "iban": "IBAN",
    "accountHolderName": "Titular de la cuenta",
    "mandateSigned": "Hay un mandato SEPA firmado",
    "mandateConfirmationHint": "El titular de la cuenta recibirá una confirmación con la referencia del mandato.",
    "storeIban": "Guardar IBAN",
    "sendStorePaymentMethodPaymentLink": "Enviar enlace para guardar método de pago",
    "cancel": "Cancelar",
    "creditCardDetails": "Datos de la tarjeta",
    "promoteToPeriodic": "Usar para facturación periódica",
    "promoteToPeriodicTitle": "¿Establecer como método de facturación periódica?",
    "promoteToPeriodicText": "Este método de pago se usará para facturación periódica. Cualquier otro método marcado para facturación periódica perderá esa designación."
  }
}
</i18n>
